<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add new two-factor authentication challenge view supporting email, SMS, and WebAuthn.
 *   - Handle method selection, code verification, and WebAuthn credential flow.
 *   - Display backup codes and call-to-action after success.
 *   - No breaking changes; new feature.
 *
 * 2026-03-11 (0.2.0) (jhoward)
 *   - Refactor for new two-factor auth flow and method selection.
 *
 * 2026-03-17 (0.2.1) (jhoward)
 *   - Show code entry UI if request for two-factor code is rate-limited (429).
 *
 * 2026-03-24 (0.3.0) (jhoward)
 *   - Add resend verification code logic and timer for email/SMS two-factor.
 *   - Show countdown and disable resend until allowed.
 *
 * 2026-03-30 (0.3.1) (jhoward)
 *   - Handle retry_after_seconds in two-factor authentication response to control resend timing.
 *
 * 2026-04-09 (0.3.2) (jhoward)
 *   - Show recipient masking for 2FA codes.
 *   - Improve resend logic and autofocus for code entry.
 *   - Use twoFactorMethodIcons and twoFactorMethodLabels for UI.
 *
 * 2026-04-29 (0.4.0) (jhoward)
 *   - Read remember_login from server and show device-remember note + opt-out checkbox.
 *   - Forward remember_device flag to all 2FA verification paths (email, SMS, WebAuthn).
 *
 * 2026-09-18 (0.5.0) (jhoward)
 *   - Give both code fields pattern="otp-6". The server has always required exactly six digits here and the field
 *     said nothing about it; the preset also brings autocomplete="one-time-code", which is what offers the code
 *     straight from the SMS that carried it.
 *
 * 2026-10-09 (0.6.0) (jhoward)
 *   - Add "Use a recovery code" and a link to the self-service two-factor reset, so a guest who has lost every
 *     method has a way back in.
 *
 * 2026-10-10 (0.7.0) (jhoward)
 *   - Give the passkey its own screen. navigator.credentials.get() ran with no catch, so cancelling the prompt was an
 *     unhandled rejection and the page said nothing; now the screen says what went wrong, in the guest's terms
 *     (webAuthnSupport.ts), and offers to try again. The Passkey tile is not shown on a browser without WebAuthn.
 *   - Plainer copy: "Verify the code we emailed you", "How would you like to confirm it's you?", and a send-failure
 *     message that no longer reads "check your sms".
 *
 * 2026-10-10 (0.8.0) (jhoward)
 *   - The Authenticator app tile goes straight to a code screen - there is nothing to send - and a 429 from too many
 *     wrong codes says how long to wait.
 *
 * 2026-10-10 (0.9.0) (jhoward)
 *   - The remember-this-device notice says what a passkey or authenticator app earns: the strong window from
 *     remember_device_days_strong, next to the standard one.
 *
 * 2026-10-15 (0.10.0) (jhoward)
 *   - Clear the resend countdown before starting another. Each resend or method switch added an interval, so the
 *     countdown ran two or three times too fast and the extras outlived the page.
 *   - After the step, fall back to ?redirect= (a path on this site only) when the server names nowhere.
 *
 * 2026-10-23 (0.11.0) (jhoward)
 *   - The Passkey tile also needs canUsePasskeysHere(): on a property domain other than the relying party's, the
 *     browser must support WebAuthn Related Origin Requests.
 *
 * 2026-10-27 (0.12.0) (jhoward)
 *   - When the guest's number has stopped texts from us - known up front from sms_opted_out, or learned when a code is
 *     refused with SMS_OPTED_OUT - show SmsOptedOutNotice under the methods: text START to our number, then try again.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import MaterialButton from '@framework/components/material/MaterialButton.vue';
import MaterialFormSwitch from '@framework/components/material/MaterialFormSwitch.vue';
import MaterialFormText from '@framework/components/material/MaterialFormText.vue';
import PeppermillBigLink from '@framework/components/PeppermillBigLink.vue';
import type { FetchResponse } from '@framework/defs/framework';
import { recursiveBase64StrToArrayBuffer } from '@framework/helpers';
import type { TemplateRef } from 'vue';
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRoute } from 'vue-router';
import CountdownCallToAction from '@/components/CountdownCallToAction.vue';
import MaterialFormContainer from '@/components/framework-proxy/MaterialFormContainer.vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import SmsOptedOutNotice from '@/components/SmsOptedOutNotice.vue';
import type { TwoFactorMethods } from '@/defs/TwoFactorAuth.d.ts';
import getWebAuthnChallenge from '@/helpers/getWebAuthnChallenge';
import getWebAuthnChallengeResponse from '@/helpers/getWebAuthnChallengeResponse';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import safeRedirectPath from '@/helpers/safeRedirectPath';
import type { SmsOptIn } from '@/helpers/smsOptedOut';
import smsOptedOut from '@/helpers/smsOptedOut';
import { canUsePasskeysHere, describeWebAuthnError } from '@/helpers/webAuthnSupport';
import router from '@/router';
import useAppStore from '@/store';
import type { LoginResponse } from '@/views/Auth/Login.vue';
import {
  requestTwoFactorCode,
  verifyRecoveryCode,
  verifyTwoFactorCode,
} from '@rss/actions/App/Http/Controllers/AuthController';
import { auth } from '@rss/routes/login/two-factor';

export interface AuthMethodsResponse extends FetchResponse {
  data: {
    methods_available: TwoFactorMethods[];
    methods_enrolled: TwoFactorMethods[];
    remember_login: boolean;
    remember_device_days: number;
    remember_device_days_strong?: number;
    /** How to text START, when the guest's number has stopped texts from us; absent or null otherwise. */
    sms_opted_out?: SmsOptIn | null;
  };
}

export interface AuthRequestResponse extends FetchResponse {
  data: {
    challenge?: string;
    retry_after_seconds?: number;
    recipient?: string;
  };
}

export interface ResendVerificationCodeResponse extends FetchResponse {
  data?: {
    retry_after_seconds?: number;
    recipient?: string;
  };
}

const route = useRoute();
const appStore = useAppStore();
const methodsAvailable = ref<TwoFactorMethods[]>([]);

/**
 * Where to go once the step is done: where the server says, else where the session ran out (?redirect=, a path on
 * this site only), else Home.
 */
function afterSignInRoute(serverRedirect: string | null | undefined): RouteLocationRaw {
  if (serverRedirect) {
    return { path: serverRedirect };
  }
  const returnPath = safeRedirectPath(route.query.redirect);

  return returnPath ? { path: returnPath } : { name: 'home' };
}
const methodsEnrolled = ref<TwoFactorMethods[]>([]);
const methodsAvailableToEnroll = ref<TwoFactorMethods[]>([]);
const isLoading = ref<boolean>(true);
const authMethod = ref<TwoFactorMethods | 'recovery' | null>(null);
const authCode = ref<string>('');

/** Whether the user checked "Remember Me" on the login page (passed back from server). */
const rememberLogin = ref<boolean>(false);
/** Whether to remember this device for 2FA — defaults to true when rememberLogin is true. */
const rememberDevice = ref<boolean>(false);

/**
 * Number of days a remembered device will stay trusted.
 * Kept in sync with config('auth.two_factor.remember_days') via the server response;
 * we default to 30 here as a safe display fallback.
 */
const rememberDeviceDays = ref<number>(30);
const rememberDeviceDaysStrong = ref<number>(90);

const rememberNotice = computed<string>(
  () =>
    `You can remember this device for ${rememberDeviceDays.value} days — ${rememberDeviceDaysStrong.value} if you ` +
    'verify with a passkey or authenticator app — and you will not be asked for two-factor authentication again on ' +
    'this device during that time.',
);

const ctaRoute = ref<RouteLocationRaw | null>(null);
const hasCodeAlreadyBeenSent = ref<boolean>(!!route.query.sent);

/** The passkey screen: waiting on the browser's prompt, or showing why the last attempt failed. */
const passkeyStatus = ref<'prompting' | 'failed' | null>(null);
const passkeyError = ref<string>('');
const canUsePasskey = ref<boolean>(false);

//A passkey tile on a browser that cannot answer it is a dead end; the note below explains the absence.
const methodsToOffer = computed<TwoFactorMethods[]>(() =>
  methodsEnrolled.value.filter((method) => 'webauthn' !== method || canUsePasskey.value),
);
const isPasskeyHidden = computed<boolean>(() => methodsEnrolled.value.includes('webauthn') && !canUsePasskey.value);

/** Set while the guest's number has stopped texts from us: the SMS tile stays, with how to turn texts back on. */
const smsOptIn = ref<SmsOptIn | null>(null);

/**
 * A code refused because the number opted out: back to the methods, where the notice says what to do. True when it
 * was, so the caller stops there.
 */
function handleSmsOptedOut(response: FetchResponse & { status?: number; data?: unknown }): boolean {
  const optIn = smsOptedOut(response);
  if (!optIn) {
    return false;
  }
  smsOptIn.value = optIn;
  authMethod.value = null;

  return true;
}

const recipient = ref<string | null>(null);
const canReSendSeconds = ref<number>(hasCodeAlreadyBeenSent.value ? 60 : 0);
let canReSendInterval = -1;

const autofocus: { [key: string]: TemplateRef } = {
  email: useTemplateRef('autofocusEmail'),
  sms: useTemplateRef('autofocusSms'),
  authenticator: useTemplateRef('autofocusAuthenticator'),
};

function focusCodeField(): void {
  requestAnimationFrame(() => {
    if (authMethod.value && authMethod.value in autofocus) {
      const autoFocusRef = autofocus[authMethod.value].value as any;
      autoFocusRef?.$el?.querySelector('input')?.focus();
    }
  });
}

async function resendVerification(method: TwoFactorMethods): Promise<void> {
  try {
    const response = await peppermillCustomFetchProxy<ResendVerificationCodeResponse>(
      {},
      {
        route: requestTwoFactorCode({ method }),
      },
    );
    if (handleSmsOptedOut(response)) {
      return;
    }
    if (response.message) {
      appStore.setNewSnack(15000, response.message, 200 === response.status ? 'check' : 'error');
    }

    if (200 === response.status) {
      hasCodeAlreadyBeenSent.value = true;
    }

    if (response.data?.retry_after_seconds) {
      canReSendSeconds.value = response.data.retry_after_seconds;
      clearInterval(canReSendInterval);
      canReSendInterval = setInterval(() => {
        if (canReSendSeconds.value > 0) {
          canReSendSeconds.value -= 1;
        }
      }, 1000);
    }

    if (response.data?.recipient) {
      recipient.value = response.data.recipient;
    }
  } catch (error) {
    console.error(error);
    appStore.setNewSnack(15000, 'An error occurred.', 'error');
  }
}

async function requestAuth(method: TwoFactorMethods) {
  if (['email', 'sms'].includes(method)) {
    appStore.isLoaderActive = true;
    const response = await peppermillCustomFetchProxy<AuthRequestResponse>(
      {},
      {
        route: requestTwoFactorCode({ method }),
      },
    );
    if (handleSmsOptedOut(response)) {
      appStore.isLoaderActive = false;
      return;
    }
    if ('sms' === method && 200 === response.status) {
      smsOptIn.value = null;
    }
    if (response.status === 200 || response.status === 429) {
      authMethod.value = method;
      hasCodeAlreadyBeenSent.value = true;
      focusCodeField();

      if (response.data?.retry_after_seconds) {
        canReSendSeconds.value = response.data.retry_after_seconds;
      } else {
        canReSendSeconds.value = 60;
      }

      clearInterval(canReSendInterval);
      canReSendInterval = setInterval(() => {
        if (canReSendSeconds.value > 0) {
          canReSendSeconds.value -= 1;
        }
      }, 1000);

      if (response.data?.recipient) {
        recipient.value = response.data.recipient;
      }
    }
    appStore.setNewSnack(
      15000,
      response.message ||
        `We could not send the code. Check your ${'sms' === method ? 'text messages' : 'email'} and try again.`,
      response.status === 200 ? 'check' : 'error',
    );
    appStore.isLoaderActive = false;
  } else if ('webauthn' === method) {
    await startPasskey();
  } else if ('authenticator' === method) {
    //Nothing to send: the app already has the code.
    authMethod.value = method;
    authCode.value = '';
    focusCodeField();
  }
}

/**
 * The passkey ceremony, start to finish. Anything that goes wrong lands on the passkey screen as a sentence the
 * guest can act on, with a button to try again; nothing here throws out of the handler.
 */
async function startPasskey(): Promise<void> {
  authMethod.value = 'webauthn';
  passkeyStatus.value = 'prompting';
  passkeyError.value = '';

  const challenge = await getWebAuthnChallenge<CredentialRequestOptions>('authentication');
  if (typeof challenge === 'string') {
    failPasskey(challenge);
    return;
  }
  if (!challenge.publicKey) {
    failPasskey('Could not start the passkey prompt.');
    return;
  }

  recursiveBase64StrToArrayBuffer(challenge);

  try {
    const credential = (await navigator.credentials.get(challenge)) as PublicKeyCredential | null;
    if (!credential) {
      failPasskey('No passkey was used. Please try again.');
      return;
    }

    const response = await getWebAuthnChallengeResponse('authentication', credential, rememberDevice.value);
    if (!response.success) {
      failPasskey(response.message);
      return;
    }

    appStore.state.user = response.user || appStore.state.user;
    appStore.csrfToken = response.csrfToken || appStore.csrfToken;
    appStore.honeypot = response.honeypot || appStore.honeypot;
    appStore.state.twoFactorAuthRequired =
      typeof response.twoFactorAuthRequired === 'boolean'
        ? response.twoFactorAuthRequired
        : appStore.state.twoFactorAuthRequired;

    appStore.setNewSnack(15000, response.message, 'check');
    passkeyStatus.value = null;
    ctaRoute.value = response.redirect ? { path: response.redirect } : { name: 'home' };
  } catch (err) {
    failPasskey(describeWebAuthnError(err, 'get'));
  }
}

function failPasskey(message: string): void {
  passkeyStatus.value = 'failed';
  passkeyError.value = message;
}

interface VerifyResponse extends LoginResponse {
  data?: LoginResponse['data'] & { retry_after_seconds?: number };
}

async function verifyAuth(method: TwoFactorMethods, code: string) {
  if (['email', 'sms', 'authenticator'].includes(method)) {
    const response = await peppermillCustomFetchProxy<VerifyResponse>(
      {
        code,
        remember_device: rememberDevice.value ? 1 : 0,
      },
      {
        route: verifyTwoFactorCode({ method }),
      },
    );

    if (429 === response.status && response.data?.retry_after_seconds) {
      appStore.setNewSnack(
        15000,
        `Too many codes tried. Wait ${response.data.retry_after_seconds} seconds and try the newest one.`,
        'error',
      );
    } else if (response.message) {
      appStore.setNewSnack(15000, response.message, 200 === response.status ? 'check' : 'error');
    }

    if (response.data && response.status === 200) {
      appStore.state.twoFactorAuthRequired = false;
      appStore.state.user = response.data?.user || appStore.state.user;
      appStore.csrfToken = response.data?.csrfToken || appStore.csrfToken;
      appStore.honeypot = response.data?.honeypot || appStore.honeypot;
      ctaRoute.value = afterSignInRoute(response.data?.redirect);
    }
  }
}

async function verifyRecovery(code: string) {
  const response = await peppermillCustomFetchProxy<LoginResponse>(
    {
      code,
      remember_device: rememberDevice.value ? 1 : 0,
    },
    {
      route: verifyRecoveryCode(),
    },
  );

  if (response.message) {
    appStore.setNewSnack(15000, response.message, 200 === response.status ? 'check' : 'error');
  }

  if (response.data && response.status === 200) {
    appStore.state.twoFactorAuthRequired = false;
    appStore.state.user = response.data?.user || appStore.state.user;
    appStore.csrfToken = response.data?.csrfToken || appStore.csrfToken;
    appStore.honeypot = response.data?.honeypot || appStore.honeypot;
    if (typeof response.data?.codes_remaining === 'number' && response.data.codes_remaining <= 2) {
      appStore.setNewSnack(
        20000,
        `You have ${response.data.codes_remaining} recovery code(s) left. Generate new ones from your account.`,
        'warning',
      );
    }
    ctaRoute.value = afterSignInRoute(response.data?.redirect);
  }
}

onUnmounted(() => {
  ctaRoute.value = null;
  clearInterval(canReSendInterval);
});

onMounted(async () => {
  if (appStore.state.user && !appStore.state.twoFactorAuthRequired) {
    appStore.setNewSnack(10000, "Already authenticated, let's get you logged in.", 'check');
    router.push({ name: 'home' });
    return;
  }

  const [response, passkeysHere] = await Promise.all([
    peppermillCustomFetchProxy<AuthMethodsResponse>(
      {},
      {
        route: auth(),
      },
    ),
    canUsePasskeysHere(),
  ]);
  canUsePasskey.value = passkeysHere;
  if (response.status === 200) {
    methodsAvailable.value = response.data.methods_available;
    methodsEnrolled.value = response.data.methods_enrolled;
    methodsAvailableToEnroll.value = response.data.methods_available.filter(
      (method) => !response.data.methods_enrolled.includes(method),
    );
    rememberLogin.value = response.data.remember_login ?? false;
    rememberDevice.value = rememberLogin.value; // default opt-out to OFF (i.e. remember by default)
    rememberDeviceDays.value = response.data.remember_device_days ?? 30;
    rememberDeviceDaysStrong.value = response.data.remember_device_days_strong ?? 90;
    smsOptIn.value = response.data.sms_opted_out ?? null;
  }
  isLoading.value = false;
});
</script>

<template>
  <peppermill-viewport>
    <peppermill-content-block v-if="isLoading">
      <p>Loading available authentication methods...</p>
    </peppermill-content-block>

    <peppermill-content-block v-else-if="ctaRoute">
      <h2>You're signed in</h2>
      <countdown-call-to-action :cta-route="ctaRoute"></countdown-call-to-action>
    </peppermill-content-block>

    <template v-else-if="authMethod === 'webauthn'">
      <a href="#" @click.prevent="authMethod = null" class="two_factor_login--back_btn"
        ><span class="material-icons">arrow_back</span>Back</a
      >
      <h1>Use your passkey</h1>
      <peppermill-content-block>
        <p v-if="passkeyStatus === 'prompting'">Follow the prompt from your browser or device.</p>
        <template v-else>
          <p>{{ passkeyError }}</p>
          <material-button label="TRY AGAIN" @click="startPasskey"></material-button>
        </template>
        <hr />
        <p class="two_factor_login--remember_notice">
          <span class="material-icons" aria-hidden="true">info</span> {{ rememberNotice }}
        </p>
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </template>

    <material-form-container
      v-else-if="authMethod === 'email'"
      hide-steps
      ignore-ajax
      @submit.prevent="verifyAuth('email', authCode)"
    >
      <a href="#" @click.prevent="authMethod = null" class="two_factor_login--back_btn"
        ><span class="material-icons">arrow_back</span>Back</a
      >
      <h1>Verify the code we emailed you</h1>
      <peppermill-content-block>
        <p>
          You will find a verification code
          <template v-if="recipient">sent to {{ recipient }}</template>
          <template v-else>in your email inbox</template>, please enter the code below to complete your login.
        </p>
        <material-form-text
          label="Code"
          name="code"
          pattern="otp-6"
          @update:value="authCode = $event"
          ref="autofocusEmail"
        ></material-form-text>
        <material-button label="VERIFY CODE" type="submit"></material-button>
        <p v-if="canReSendSeconds > 0">You can request another verification code in {{ canReSendSeconds }} seconds.</p>
        <p v-else>
          You can request another verification code once every minute by
          <a href="#" @click.prevent="resendVerification('email')">clicking here</a>.
        </p>
        <hr />
        <p class="two_factor_login--remember_notice">
          <span class="material-icons" aria-hidden="true">info</span> {{ rememberNotice }}
        </p>
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </material-form-container>

    <material-form-container
      v-else-if="authMethod === 'sms'"
      hide-steps
      ignore-ajax
      @submit.prevent="verifyAuth('sms', authCode)"
    >
      <a href="#" @click.prevent="authMethod = null" class="two_factor_login--back_btn"
        ><span class="material-icons">arrow_back</span>Back</a
      >
      <h1>Verify the code we texted you</h1>
      <peppermill-content-block>
        <p>
          You will find a verification code
          <template v-if="recipient">sent to {{ recipient }}</template>
          <template v-else>in your text messages</template>, please enter the code below to complete your login.
        </p>
        <material-form-text
          label="Code"
          name="code"
          pattern="otp-6"
          @update:value="authCode = $event"
          ref="autofocusSms"
        ></material-form-text>
        <material-button label="VERIFY CODE" type="submit"></material-button>
        <p v-if="canReSendSeconds > 0">You can request another verification code in {{ canReSendSeconds }} seconds.</p>
        <p v-else>
          You can request another verification code once every minute by
          <a href="#" @click.prevent="resendVerification('sms')">clicking here</a>.
        </p>
        <hr />
        <p class="two_factor_login--remember_notice">
          <span class="material-icons" aria-hidden="true">info</span> {{ rememberNotice }}
        </p>
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </material-form-container>

    <material-form-container
      v-else-if="authMethod === 'authenticator'"
      hide-steps
      ignore-ajax
      @submit.prevent="verifyAuth('authenticator', authCode)"
    >
      <a href="#" @click.prevent="authMethod = null" class="two_factor_login--back_btn"
        ><span class="material-icons">arrow_back</span>Back</a
      >
      <h1>Enter your authenticator code</h1>
      <peppermill-content-block>
        <p>Open your authenticator app and enter the 6-digit code it shows. Codes change every 30 seconds.</p>
        <material-form-text
          label="Code"
          name="code"
          pattern="otp-6"
          @update:value="authCode = $event"
          ref="autofocusAuthenticator"
        ></material-form-text>
        <material-button label="VERIFY CODE" type="submit"></material-button>
        <hr />
        <p class="two_factor_login--remember_notice">
          <span class="material-icons" aria-hidden="true">info</span> {{ rememberNotice }}
        </p>
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </material-form-container>

    <material-form-container
      v-else-if="authMethod === 'recovery'"
      hide-steps
      ignore-ajax
      @submit.prevent="verifyRecovery(authCode)"
    >
      <a href="#" @click.prevent="authMethod = null" class="two_factor_login--back_btn"
        ><span class="material-icons">arrow_back</span>Back</a
      >
      <h1>Use a Recovery Code</h1>
      <peppermill-content-block>
        <p>
          Enter one of the recovery codes you saved when you set up two-factor authentication. Each code works once.
        </p>
        <material-form-text
          label="Recovery code"
          name="code"
          autocomplete="off"
          @update:value="authCode = $event"
        ></material-form-text>
        <material-button label="VERIFY CODE" type="submit"></material-button>
        <hr />
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </material-form-container>

    <peppermill-content-block v-else-if="methodsEnrolled.length === 0">
      <p>
        You do not have any two-factor authentication methods enrolled. Please enroll in at least one method to secure
        your account.
      </p>
      <p>
        <a
          href="#"
          @click.prevent="
            authMethod = 'recovery';
            authCode = '';
          "
          >Use a recovery code</a
        >
        or <router-link :to="{ name: 'login.help.two-factor' }">reset your two-factor authentication</router-link>.
      </p>
    </peppermill-content-block>

    <template v-else>
      <p>How would you like to confirm it's you?</p>
      <peppermill-content-block is-big-link-container>
        <peppermill-big-link
          v-for="method in methodsToOffer"
          :key="method"
          is-anchor
          path="#"
          @click.prevent="requestAuth(method)"
          :title="appStore.twoFactorMethodLabels[method] || ''"
          :icon="appStore.twoFactorMethodIcons[method] || ''"
        ></peppermill-big-link>
      </peppermill-content-block>

      <peppermill-content-block v-if="smsOptIn && methodsToOffer.includes('sms')">
        <sms-opted-out-notice
          :opt-in="smsOptIn"
          retry-label="Try again"
          @retry="requestAuth('sms')"
        ></sms-opted-out-notice>
      </peppermill-content-block>

      <peppermill-content-block>
        <p v-if="isPasskeyHidden">This browser can't use your passkey on this site, so it isn't offered here.</p>
        <p>
          Can't use any of these?
          <a
            href="#"
            @click.prevent="
              authMethod = 'recovery';
              authCode = '';
            "
            >Use a recovery code</a
          >
          or <router-link :to="{ name: 'login.help.two-factor' }">reset your two-factor authentication</router-link>.
        </p>
        <p class="two_factor_login--remember_notice">
          <span class="material-icons" aria-hidden="true">info</span> {{ rememberNotice }}
        </p>
        <material-form-switch
          label="Remember this device"
          name="remember_device"
          value="1"
          :is-checked="rememberDevice"
          @update:value="rememberDevice = $event"
        ></material-form-switch>
      </peppermill-content-block>
    </template>
  </peppermill-viewport>
</template>

<style scoped>
.two_factor_login--back_btn {
  display: flex;
  margin-bottom: 0.25em;
  text-decoration: none;
  align-items: center;
}

.two_factor_login--remember_notice {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}
</style>
